'use client';

import { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function getInitialTheme(): { theme: Theme; resolved: 'light' | 'dark' } {
  if (typeof window === 'undefined') return { theme: 'light', resolved: 'light' };
  const stored = (localStorage.getItem('theme') || document.cookie.match(/theme=(light|dark|system)/)?.[1]) as Theme | null;
  const theme = stored || 'light';
  let resolved: 'light' | 'dark';
  if (theme === 'system') {
    resolved = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } else {
    resolved = theme;
  }
  return { theme, resolved };
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [init] = useState(getInitialTheme);
  const [theme, setThemeState] = useState<Theme>(init.theme);
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(init.resolved);

  useEffect(() => {
    const root = document.documentElement;
    
    const updateTheme = () => {
      let resolved: 'light' | 'dark';
      
      if (theme === 'system') {
        resolved = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      } else {
        resolved = theme;
      }
      
      setResolvedTheme(resolved);
      root.classList.remove('light', 'dark');
      root.classList.add(resolved);
    };

    updateTheme();

    if (theme === 'system') {
      const media = window.matchMedia('(prefers-color-scheme: dark)');
      media.addEventListener('change', updateTheme);
      return () => media.removeEventListener('change', updateTheme);
    }
  }, [theme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem('theme', newTheme);
    document.cookie = `theme=${newTheme};path=/;max-age=31536000;SameSite=Lax`;
  };

  // Allow the URL_ACTIONS `?theme=` action (and any other off-tree
  // dispatcher) to flip the theme without holding a context handle.
  // The event payload is validated by the URL action so an unknown
  // value can't reach here, but we double-check anyway since the
  // event is dispatchable from anywhere on `window`.
  useEffect(() => {
    const onSet = (e: Event) => {
      const ev = e as CustomEvent<{ theme?: string }>;
      const next = ev.detail?.theme;
      if (next === 'light' || next === 'dark' || next === 'system') {
        setTheme(next);
      }
    };
    window.addEventListener('robutler:set-theme', onSet);
    return () => window.removeEventListener('robutler:set-theme', onSet);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
